/*
 * PDP 2026 – particle chart (<pdp-particles-chart>, used by sections/pdp-particles.liquid).
 *
 * Purpose: selecting a particle – from the list (desktop) or from a chart bar (the only control on mobile,
 * where the list is hidden) – marks it as pressed everywhere and shows its description.
 * Bars are SVG groups with role="button"; while the list is visible they are taken out of the tab order
 * (tabindex="-1", still clickable) so keyboard users don't meet every particle twice.
 *
 * Config (read from the markup, no options):
 *   [data-particle="<block id>"]       list buttons and chart bars (aria-pressed is kept in sync)
 *   [data-particle-desc="<block id>"]  descriptions; inactive ones get the `hidden` attribute
 *   [data-particle-list]               the particle list – visible from DESKTOP_QUERY up
 *
 * Usage:
 *   <script src="{{ 'pdp-particles.js' | asset_url }}" defer></script>
 *   <pdp-particles-chart> …list, svg bars, descriptions… </pdp-particles-chart>
 * Works with several instances per page and re-initialises when the theme editor reloads the section.
 */

if (!customElements.get('pdp-particles-chart')) {
  /** Media query from which the particle list is visible (matches assets/pdp-particles.css). */
  const DESKTOP_QUERY = '(min-width: 799px)';

  class PdpParticlesChart extends HTMLElement {
    /**
     * Collects the parts and binds the listeners.
     * @returns {void}
     */
    connectedCallback() {
      this.controls = Array.from(this.querySelectorAll('[data-particle]'));
      this.bars = this.controls.filter((control) => control.getAttribute('role') === 'button');
      this.descriptions = Array.from(this.querySelectorAll('[data-particle-desc]'));
      this.mediaQuery = window.matchMedia(DESKTOP_QUERY);

      if (this.controls.length === 0) {
        return;
      }

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onMediaChange = this.onMediaChange.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);

      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      this.mediaQuery.addEventListener('change', this.onMediaChange);
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      this.onMediaChange();
    }

    /**
     * Removes the listeners (section removed or re-rendered in the theme editor).
     * @returns {void}
     */
    disconnectedCallback() {
      if (!this.onClick) {
        return;
      }

      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      this.mediaQuery.removeEventListener('change', this.onMediaChange);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    /**
     * Marks a particle as pressed in the list and the chart and shows its description.
     * @param {string} id - Block id of the particle.
     * @returns {void}
     */
    select(id) {
      const hasDescription = this.descriptions.some((desc) => desc.dataset.particleDesc === id);

      if (!id || !hasDescription) {
        return;
      }

      this.controls.forEach((control) => {
        control.setAttribute('aria-pressed', String(control.dataset.particle === id));
      });

      this.descriptions.forEach((desc) => {
        desc.hidden = desc.dataset.particleDesc !== id;
      });
    }

    /**
     * Handles clicks on list buttons and chart bars.
     * @param {MouseEvent} event - Click event.
     * @returns {void}
     */
    onClick(event) {
      const control = event.target instanceof Element ? event.target.closest('[data-particle]') : null;

      if (!control || !this.contains(control)) {
        return;
      }

      this.select(control.dataset.particle);
    }

    /**
     * Makes the SVG bars (role="button") work with Enter and Space like native buttons.
     * @param {KeyboardEvent} event - Keydown event.
     * @returns {void}
     */
    onKeydown(event) {
      const bar = event.target instanceof Element ? event.target.closest('[data-particle][role="button"]') : null;

      if (!bar || (event.key !== 'Enter' && event.key !== ' ')) {
        return;
      }

      event.preventDefault();
      this.select(bar.dataset.particle);
    }

    /**
     * Keeps the bars out of the tab order while the list (same controls) is visible.
     * @returns {void}
     */
    onMediaChange() {
      const tabIndex = this.mediaQuery.matches ? '-1' : '0';

      this.bars.forEach((bar) => {
        bar.setAttribute('tabindex', tabIndex);
      });
    }

    /**
     * Theme editor: selecting a particle block shows it in the chart.
     * @param {CustomEvent} event - shopify:block:select event.
     * @returns {void}
     */
    onBlockSelect(event) {
      if (!(event.target instanceof Element) || !this.contains(event.target)) {
        return;
      }

      this.select(event.detail.blockId);
    }
  }

  customElements.define('pdp-particles-chart', PdpParticlesChart);
}
